import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { initialAsync, postalCodeAsync, signupAsync } from "./operations";
import { SignupState } from "./state";
import { PostalError } from "./types";

const initialState: SignupState = {
  submitting: false,
  apps: {
    pageState: "input",
    email: "",
    emailCheck: "",
    password: "",
    passwordCheck: "",
    country: "",
    familyName: "",
    firstName: "",
    familyNameFurigana: "",
    firstNameFurigana: "",
    company: "",
    companyFurigana: "",
    office: "",
    department: "",
    position: "",
    jobType: "",
    companyTel: "",
    postalCode: "",
    state: "",
    city: "",
    address: "",
    buildingName: "",
    postalError: "Success",
    loadState: "Loading",
    reCaptchaSiteKey: "",
    market_status: false,
  },
};

export const signupSlice = createSlice({
  name: "signup",
  initialState,
  reducers: {
    changePageState: (
      state,
      action: PayloadAction<{ pageState: "input" | "confirm" }>
    ) => {
      const { pageState } = action.payload;
      state.apps.pageState = pageState;
    },
    changeEmail: (state, action: PayloadAction<{ email: string }>) => {
      const { email } = action.payload;
      state.apps.email = email;
    },
    changeEmailCheck: (
      state,
      action: PayloadAction<{ emailCheck: string }>
    ) => {
      const { emailCheck } = action.payload;
      state.apps.emailCheck = emailCheck;
    },
    changePassword: (state, action: PayloadAction<{ password: string }>) => {
      const { password } = action.payload;
      state.apps.password = password;
    },
    changePasswordCheck: (
      state,
      action: PayloadAction<{ passwordCheck: string }>
    ) => {
      const { passwordCheck } = action.payload;
      state.apps.passwordCheck = passwordCheck;
    },
    changeCountry: (state, action: PayloadAction<{ country: string }>) => {
      const { country } = action.payload;
      state.apps.country = country;
    },
    changeFamilyName: (
      state,
      action: PayloadAction<{ familyName: string }>
    ) => {
      const { familyName } = action.payload;
      state.apps.familyName = familyName;
    },
    changeFirstName: (state, action: PayloadAction<{ firstName: string }>) => {
      const { firstName } = action.payload;
      state.apps.firstName = firstName;
    },
    changeFamilyNameFurigana: (
      state,
      action: PayloadAction<{ familyNameFurigana: string }>
    ) => {
      const { familyNameFurigana } = action.payload;
      state.apps.familyNameFurigana = familyNameFurigana;
    },
    changeFirstNameFurigana: (
      state,
      action: PayloadAction<{ firstNameFurigana: string }>
    ) => {
      const { firstNameFurigana } = action.payload;
      state.apps.firstNameFurigana = firstNameFurigana;
    },
    changeCompany: (state, action: PayloadAction<{ company: string }>) => {
      const { company } = action.payload;
      state.apps.company = company;
    },
    changeCompanyFurigana: (
      state,
      action: PayloadAction<{ companyFurigana: string }>
    ) => {
      const { companyFurigana } = action.payload;
      state.apps.companyFurigana = companyFurigana;
    },
    changeOffice: (state, action: PayloadAction<{ office: string }>) => {
      const { office } = action.payload;
      state.apps.office = office;
    },
    changeDepartment: (
      state,
      action: PayloadAction<{ department: string }>
    ) => {
      const { department } = action.payload;
      state.apps.department = department;
    },
    changePosition: (state, action: PayloadAction<{ position: string }>) => {
      const { position } = action.payload;
      state.apps.position = position;
    },
    changeJobType: (state, action: PayloadAction<{ jobType: string }>) => {
      const { jobType } = action.payload;
      state.apps.jobType = jobType;
    },
    changeCompanyTel: (
      state,
      action: PayloadAction<{ companyTel: string }>
    ) => {
      const { companyTel } = action.payload;
      state.apps.companyTel = companyTel;
    },
    changePostalCode: (
      state,
      action: PayloadAction<{ postalCode: string }>
    ) => {
      const { postalCode } = action.payload;
      state.apps.postalCode = postalCode;
    },
    changeState: (states, action: PayloadAction<{ state: string }>) => {
      const { state } = action.payload;
      states.apps.state = state;
    },
    changeCity: (state, action: PayloadAction<{ city: string }>) => {
      const { city } = action.payload;
      state.apps.city = city;
    },
    changeAddress: (state, action: PayloadAction<{ address: string }>) => {
      const { address } = action.payload;
      state.apps.address = address;
    },
    changeBuildingName: (
      state,
      action: PayloadAction<{ buildingName: string }>
    ) => {
      const { buildingName } = action.payload;
      state.apps.buildingName = buildingName;
    },
    changeMarket_Status: (
      state,
      action: PayloadAction<{ market_status: boolean }>
    ) => {
      const { market_status } = action.payload;
      state.apps.market_status = market_status;
    },
    changePostalError: (
      state,
      action: PayloadAction<{ postalError: PostalError }>
    ) => {
      const { postalError } = action.payload;
      state.apps.postalError = postalError;
    },
    cleanupByCountyChange: (state) => {
      state.apps.firstNameFurigana = "";
      state.apps.familyNameFurigana = "";
      state.apps.companyFurigana = "";
      state.apps.state = "";
      state.apps.city = "";
      state.apps.address = "";
      state.apps.buildingName = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initialAsync.fulfilled, (state, action) => {
      const { sitekey } = action.payload;
      state.apps.reCaptchaSiteKey = sitekey;
    });
    builder.addCase(postalCodeAsync.pending, () => {
      /* empty function */
    });
    builder.addCase(postalCodeAsync.fulfilled, (state, action) => {
      const { payload } = action;
      if (payload?.status === 400) {
        state.apps.postalError = "BadRequest";
        state.apps.state = "";
        state.apps.city = "";
        return;
      }
      if (payload?.status === 500 || payload?.results?.length !== 1) {
        state.apps.postalError = "Error";
        state.apps.state = "";
        state.apps.city = "";
        return;
      }
      state.apps.postalError = "Success";
      state.apps.state = payload.results[0].address1;
      state.apps.city =
        payload.results[0].address2 + payload.results[0].address3;
    });
    builder.addCase(postalCodeAsync.rejected, (state) => {
      state.apps.postalError = "Error";
      state.apps.state = "";
      state.apps.city = "";
    });
    builder.addCase(signupAsync.pending, (state) => {
      state.submitting = true;
    });
    builder.addCase(signupAsync.fulfilled, (state) => {
      state.apps.pageState = 'complete';
      state.submitting = false;
    });
    builder.addCase(signupAsync.rejected, (state) => {
      state.submitting = false;
    });
  },
});
export const {
  changePageState,
  changeEmail,
  changeEmailCheck,
  changePassword,
  changePasswordCheck,
  changeCountry,
  changeFamilyName,
  changeFirstName,
  changeFamilyNameFurigana,
  changeFirstNameFurigana,
  changeCompany,
  changeCompanyFurigana,
  changeOffice,
  changeDepartment,
  changePosition,
  changeJobType,
  changeCompanyTel,
  changePostalCode,
  changeState,
  changeCity,
  changeAddress,
  changeBuildingName,
  changeMarket_Status,
  cleanupByCountyChange,
} = signupSlice.actions;
export default signupSlice.reducer;
